import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { io, Socket } from 'socket.io-client'
import { useAuthStore } from '../services/auth'

// Dynamic API base URL that works with custom domains
const API_BASE_URL = (() => {
  // In development, always use localhost
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:5001';
  }
  
  // In production, check if we're on a custom domain
  const currentDomain = window.location.hostname;
  const mainDomains = [
    'localhost',
    'charity-auctions-web-app.onrender.com',
    'auctions.decoded.digital',
  ];
  
  // If we're on a custom domain, use the current domain for API calls
  if (!mainDomains.includes(currentDomain)) {
    return window.location.origin;
  }
  
  // If we're on the main domain, use the configured API base URL or current origin
  return process.env.REACT_APP_API_BASE_URL || window.location.origin;
})()

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
})

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

interface SocketProviderProps {
  children: ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const { user, impersonation } = useAuthStore()

  // Track impersonation state changes and reconnect socket
  useEffect(() => {
    if (socket && socket.connected) {
      // Impersonation state changed, reconnecting socket
      socket.disconnect()
      // Add delay to prevent rapid reconnections
      setTimeout(() => {
        if (socket && !socket.connected) {
          socket.connect()
        }
      }, 500)
    }
  }, [impersonation?.isImpersonating, socket])

  useEffect(() => {
    // Only create socket connection if user is authenticated
    if (!user) {
      // No user authenticated, skipping socket connection
      return
    }

    // Creating new socket connection for authenticated user
    
    const socketInstance = io(`${API_BASE_URL}`, {
      // Polling first, deliberately. A Render static site rewrite cannot carry
      // a WebSocket upgrade - it answers the handshake with a 500 - so leading
      // with websocket means a failed request on every connect in production.
      // Polling connects immediately everywhere, and the upgrade below still
      // promotes the connection to websocket wherever one is actually possible,
      // such as local development talking straight to the API.
      transports: ['polling', 'websocket'],
      // Add reconnection settings
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 20000,
      // Prevent multiple connections
      forceNew: false,
      // Add auth if needed
      autoConnect: true,
      // Add credentials for authentication
      withCredentials: true,
      // Deployed, the API is reached through the static site's rewrite, which
      // answers a WebSocket upgrade with a 500. Attempting one anyway costs a
      // failed request per connection and prints a console error on every page,
      // for an upgrade that can never succeed. In development the client talks
      // to the API directly, where a real WebSocket is available and worth having.
      upgrade: process.env.NODE_ENV !== 'production',
      rememberUpgrade: false,
      // Add error handling
      closeOnBeforeunload: false,
    })

    socketInstance.on('connect', () => {
      // Socket connected
      setIsConnected(true)
    })

    socketInstance.on('disconnect', (reason) => {
      // Socket disconnected
      setIsConnected(false)
    })

    socketInstance.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error)
      setIsConnected(false)
      
      // If authentication error, try to reconnect after a delay
      if (error.message?.includes('Authentication') || error.message?.includes('session')) {
        setTimeout(() => {
          if (socketInstance && !socketInstance.connected) {
            socketInstance.connect()
          }
        }, 2000)
      }
    })

    socketInstance.on('reconnect', (attemptNumber) => {
      // Socket reconnected
      setIsConnected(true)
    })

    socketInstance.on('reconnect_error', (error) => {
      // Socket reconnection error
    })

    socketInstance.on('reconnect_attempt', (attemptNumber) => {
      // Socket reconnection attempt
    })

    setSocket(socketInstance)

    // Cleanup function
    return () => {
      // Cleaning up socket connection
      if (socketInstance) {
        socketInstance.removeAllListeners()
        socketInstance.disconnect()
      }
      setSocket(null)
      setIsConnected(false)
    }
  }, [user]) // Re-run when user authentication changes

  const value = {
    socket,
    isConnected,
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}
